import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { fmtBytes, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { TwoFactorGate, twoFactorError } from "@/components/TwoFactor";
import StatusBadge, { Bool } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Plug, ScanSearch, ArrowLeft, Table2, Eye, Code2, Calendar, Zap, ShieldCheck } from "lucide-react";

const CAP_KEYS = [
  ["can_connect", "capCanConnect"], ["can_list_databases", "capCanListDb"],
  ["can_dump_schema", "capCanDumpSchema"], ["can_dump_data", "capCanDumpData"],
  ["can_dump_views", "capCanDumpViews"], ["can_dump_routines", "capCanDumpRoutines"],
  ["can_dump_events", "capCanDumpEvents"], ["can_dump_triggers", "capCanDumpTriggers"],
  ["can_inspect_binlogs", "capCanInspectBinlogs"], ["binlog_enabled", "capBinlogEnabled"],
  ["pitr_possible", "capPitr"], ["possible_full_logical_backup", "capFullLogical"],
];

export default function SourceDetail() {
  const { id } = useParams();
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [source, setSource] = useState(null);
  const [scan, setScan] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [filter, setFilter] = useState("");
  const [preflight, setPreflight] = useState(null);
  const [pfBusy, setPfBusy] = useState(false);
  const [protectionBusy, setProtectionBusy] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);
  const canManage = user?.role === "admin" || user?.role === "operator";

  const setProtection = async (enabled, otp) => {
    if (!canManage || protectionBusy) return;
    setProtectionBusy(true);
    try {
      const r = await api.post(`/sources/${id}/protection`, { enabled, otp });
      setSource((s) => ({ ...s, write_protected: r.data.write_protected }));
      setGateOpen(false);
      toast.success(t(enabled ? "protection.enabled" : "protection.disabled"));
    } catch (e) { toast.error(twoFactorError(e, t, "protection.failed")); }
    finally { setProtectionBusy(false); }
  };

  const load = useCallback(async () => {
    try {
      const s = await api.get(`/sources/${id}`);
      setSource(s.data);
      if (s.data.source_mode === "ssh_mariadb_host") {
        try { const p = await api.get(`/sources/${id}/preflight`); setPreflight(p.data.report); } catch { setPreflight(null); }
      } else {
        try { const sc = await api.get(`/sources/${id}/scan`); setScan(sc.data); } catch { setScan(null); }
      }
    } catch { toast.error(t("sourceDetail.loadFailed")); }
  }, [id, t]);
  useEffect(() => { load(); }, [load]);

  const doPreflight = async () => {
    setPfBusy(true);
    try {
      const r = await api.post(`/sources/${id}/preflight`);
      setPreflight(r.data.report);
      r.data.report.ok ? toast.success(t("sourceDetail.preflightPassed")) : toast.warning(t("sourceDetail.preflightIssues"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("sourceDetail.preflightFailed")); }
    finally { setPfBusy(false); }
  };

  const doScan = async () => {
    setScanning(true);
    try {
      const r = await api.post(`/sources/${id}/scan`);
      setScan(r.data);
      toast.success(t("sourceDetail.scanComplete"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("sourceDetail.scanFailed")); }
    finally { setScanning(false); }
  };

  const doTest = async () => {
    try {
      const ep = source.source_mode === "ssh_mariadb_host" ? `/sources/${id}/ssh-test` : `/sources/${id}/test`;
      const r = await api.post(ep);
      r.data.ok
        ? toast.success(source.source_mode === "ssh_mariadb_host" ? t("sourceDetail.sshOk", { os: r.data.os || "", arch: r.data.arch || "" }) : t("sourceDetail.connected", { version: r.data.version }))
        : toast.error(r.data.error || t("sourceDetail.failed"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("sourceDetail.testFailed")); }
  };

  if (!source) return <div className="p-8 font-mono text-sm text-muted-foreground spinner-dots">{t("common.loading")}</div>;
  const isSSH = source.source_mode === "ssh_mariadb_host";
  const report = scan?.report;
  const dbs = (report?.databases || []).filter((d) =>
    !filter || d.name.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div data-testid="source-detail-page">
      <TwoFactorGate open={gateOpen} onOpenChange={setGateOpen} busy={protectionBusy}
        title={t("protection.disableTitle")} description={t("protection.disableDesc", { name: source.name })}
        onVerified={(otp) => setProtection(false, otp)} />
      <PageHeader
        title={source.name}
        subtitle={isSSH ? `ssh ${source.ssh_username}@${source.ssh_host}:${source.ssh_port} · ${source.runtime_mode}` : `${source.host}:${source.port} · ${source.username}`}
        badge={<StatusBadge status={isSSH ? (source.physical_supported ? "ok" : "untested") : (source.credential_status || "untested")} />}
        actions={
          <>
            <Button variant="outline" className="rounded-none" onClick={() => navigate("/sources")}>
              <ArrowLeft className="h-4 w-4 mr-1" /> {t("common.back")}
            </Button>
            <Button variant="outline" className="rounded-none uppercase" onClick={doTest} data-testid="detail-test-button">
              <Plug className="h-4 w-4 mr-1.5" /> {isSSH ? t("sourceDetail.testSsh") : t("sourceDetail.test")}
            </Button>
            {isSSH ? (
              <Button className="rounded-none uppercase bg-[#0A0A0A] hover:bg-[#0052FF]" onClick={doPreflight} disabled={pfBusy} data-testid="detail-preflight-button">
                <ShieldCheck className="h-4 w-4 mr-1.5" /> {pfBusy ? t("sourceDetail.running") : t("sourceDetail.runPreflight")}
              </Button>
            ) : (
              <Button className="rounded-none uppercase bg-[#0A0A0A] hover:bg-[#0052FF]" onClick={doScan} disabled={scanning} data-testid="detail-scan-button">
                <ScanSearch className="h-4 w-4 mr-1.5" /> {scanning ? t("sourceDetail.scanning") : t("sourceDetail.scan")}
              </Button>
            )}
          </>
        }
      />
      <div className="p-6 md:p-8 space-y-6">
        <div className="grid-panel p-5" data-testid="source-protection-card">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="font-display font-bold">{t("protection.title")}</h3>
              <p className="text-sm text-muted-foreground mt-1">{t("protection.desc")}</p>
              <p className="text-sm mt-2" data-testid="source-protection-status">
                {t(source.write_protected !== false ? "protection.statusOn" : "protection.statusOff")}
              </p>
            </div>
            <Switch checked={source.write_protected !== false} disabled={!canManage || protectionBusy}
              aria-label={t("protection.title")} data-testid="source-protection-toggle"
              onCheckedChange={(enabled) => enabled ? setProtection(true) : setGateOpen(true)} />
          </div>
        </div>
        {isSSH ? (
          preflight ? <PreflightReport report={preflight} /> : (
            <div className="grid-panel p-16 text-center" data-testid="preflight-empty">
              <p className="text-muted-foreground">{t("sourceDetail.noPreflight")}</p>
              <Button onClick={doPreflight} disabled={pfBusy} className="mt-4 rounded-none uppercase" data-testid="empty-preflight-button">
                {pfBusy ? t("sourceDetail.running") : t("sourceDetail.runPreflightSmall")}
              </Button>
            </div>
          )
        ) : !report ? (
          <div className="grid-panel p-16 text-center">
            <p className="text-muted-foreground">{t("sourceDetail.noReport")}</p>
            <Button onClick={doScan} disabled={scanning} className="mt-4 rounded-none uppercase" data-testid="empty-scan-button">
              {scanning ? t("sourceDetail.scanning") : t("sourceDetail.scanNow")}
            </Button>
          </div>
        ) : (
          <>
            {/* Capability report */}
            <div className="grid-panel" data-testid="capability-report">
              <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                <h3 className="font-display font-bold">{t("sourceDetail.capReport")}</h3>
                <span className="font-mono text-xs text-muted-foreground">
                  {t("sourceDetail.scannedAt", { version: report.server_version, date: fmtDate(scan.created_at) })}
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-border">
                {CAP_KEYS.map(([k, labelKey]) => (
                  <div key={k} className="bg-card px-5 py-3 flex items-center justify-between" data-testid={`cap-${k}`}>
                    <span className="text-sm">{t(`sourceDetail.${labelKey}`)}</span>
                    <Bool value={!!report[k]} />
                  </div>
                ))}
              </div>
              {report.limitations?.length > 0 && (
                <div className="px-5 py-4 border-t border-border bg-yellow-50">
                  <p className="font-mono text-[11px] uppercase tracking-wide text-yellow-800 mb-2">{t("sourceDetail.limitations")}</p>
                  <ul className="space-y-1 text-sm text-yellow-900 list-disc list-inside">
                    {report.limitations.map((l, i) => <li key={i}>{l}</li>)}
                  </ul>
                </div>
              )}
            </div>

            {/* Object tree */}
            <div className="grid-panel">
              <div className="px-5 py-3 border-b border-border flex items-center justify-between gap-4">
                <h3 className="font-display font-bold">{t("sourceDetail.accessibleObjects")}</h3>
                <Input placeholder={t("sourceDetail.filterDbs")} value={filter} onChange={(e) => setFilter(e.target.value)}
                  className="rounded-none h-8 max-w-xs" data-testid="object-filter" />
              </div>
              <div className="p-3">
                <Accordion type="multiple" className="space-y-1">
                  {dbs.map((d) => (
                    <AccordionItem key={d.name} value={d.name} className="border border-border" data-testid={`db-node-${d.name}`}>
                      <AccordionTrigger className="px-4 py-2.5 hover:no-underline">
                        <div className="flex items-center gap-3 text-left">
                          <span className="font-mono font-semibold">{d.name}</span>
                          {d.is_system && <span className="font-mono text-[10px] uppercase bg-yellow-100 text-yellow-800 px-1.5 py-0.5">{t("sourceDetail.system")}</span>}
                          <span className="font-mono text-xs text-muted-foreground">
                            {t("sourceDetail.dbMeta", { tables: d.tables.length, views: d.views.length, routines: d.routines.length })}
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-3">
                        <div className="tree-line ml-2 pl-4 space-y-3">
                          <ObjGroup icon={Table2} title={t("sourceDetail.groupTables")} items={d.tables.map((t2) => ({
                            name: t2.name, meta: `${t2.engine || "?"} · ${t2.rows ?? "?"} rows · ${fmtBytes(t2.size)}`,
                            warn: t2.engine && t2.engine.toLowerCase() !== "innodb",
                          }))} />
                          <ObjGroup icon={Eye} title={t("sourceDetail.groupViews")} items={d.views.map((v) => ({ name: v.name }))} />
                          <ObjGroup icon={Code2} title={t("sourceDetail.groupRoutines")} items={d.routines.map((r) => ({ name: r.name, meta: r.type }))} />
                          <ObjGroup icon={Zap} title={t("sourceDetail.groupTriggers")} items={d.triggers.map((tr) => ({ name: tr.name, meta: tr.table }))} />
                          <ObjGroup icon={Calendar} title={t("sourceDetail.groupEvents")} items={d.events.map((e) => ({ name: e.name }))} />
                          {d.errors?.length > 0 && (
                            <p className="font-mono text-[11px] text-destructive">⚠ {d.errors.join("; ")}</p>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PreflightReport({ report }) {
  const { t } = useTranslation();
  const ssh = report.ssh || {};
  const priv = report.privilege || {};
  const tools = report.tools || {};
  const ver = report.tool_versions || {};
  const db = report.mariadb || {};
  const caps = report.capabilities || {};
  const capRows = [
    ["physical_backup_supported", t("sourceDetail.pfPhysical")],
    ["incremental_supported", t("sourceDetail.pfIncremental")],
    ["restore_test_supported", t("sourceDetail.pfRestoreTest")],
    ["logical_fallback_supported", t("sourceDetail.pfLogicalFallback")],
  ];
  return (
    <div className="space-y-6" data-testid="preflight-report">
      <div className={`grid-panel ${report.ok ? "" : "border-yellow-400"}`}>
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <h3 className="font-display font-bold">{t("sourceDetail.preflightCaps")}</h3>
          <StatusBadge status={report.ok ? "ok" : "issues"} label={report.ok ? t("sourceDetail.ready") : t("sourceDetail.issues")} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border">
          {capRows.map(([k, label]) => (
            <div key={k} className="bg-card px-5 py-3 flex items-center justify-between" data-testid={`pf-cap-${k}`}>
              <span className="text-sm">{label}</span><Bool value={!!caps[k]} />
            </div>
          ))}
        </div>
      </div>

      {report.missing?.length > 0 && (
        <div className="grid-panel border-destructive" data-testid="preflight-missing">
          <div className="px-5 py-3 border-b border-border"><h3 className="font-display font-bold text-destructive">{t("sourceDetail.missingReq")}</h3></div>
          <ul className="divide-y divide-border">
            {report.missing.map((m, i) => (
              <li key={i} className="px-5 py-3">
                <p className="font-mono text-[11px] uppercase text-destructive">{m.category}</p>
                <p className="text-sm">{m.message}</p>
              </li>
            ))}
          </ul>
        </div>
      )}
      {report.warnings?.length > 0 && (
        <div className="grid-panel">
          <div className="px-5 py-3 border-b border-border bg-yellow-50"><p className="font-mono text-[11px] uppercase text-yellow-800">{t("sourceDetail.warnings")}</p></div>
          <ul className="px-5 py-3 text-sm text-yellow-900 list-disc list-inside space-y-1">
            {report.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <InfoPanel title={t("sourceDetail.sshPriv")} rows={[
          [t("sourceDetail.reachable"), <Bool value={ssh.reachable} key="r" />],
          [t("sourceDetail.osArch"), `${ssh.os || "?"} · ${ssh.arch || "?"}`],
          [t("sourceDetail.userUid"), `${ssh.user || "?"} (${ssh.uid ?? "?"})`],
          [t("sourceDetail.tmpWritable"), <Bool value={ssh.tmp_writable} key="t" />],
          [t("sourceDetail.tmpFree"), ssh.tmp_free_kb ? `${Math.round(ssh.tmp_free_kb / 1024)} MB` : "?"],
          [t("sourceDetail.root"), <Bool value={priv.is_root} key="ro" />],
          [t("sourceDetail.sudoAvail"), <Bool value={priv.sudo_available} key="s" />],
          [t("sourceDetail.sudoNopasswd"), <Bool value={priv.sudo_nopasswd} key="n" />],
          [t("sourceDetail.fingerprint"), <span key="f" className="font-mono text-[10px] break-all">{ssh.host_key_fingerprint}</span>],
        ]} />
        <InfoPanel title={t("sourceDetail.mariadb")} rows={[
          [t("sourceDetail.reachable"), <Bool value={db.reachable} key="r" />],
          [t("sourceDetail.version"), db.version || "?"],
          [t("sourceDetail.isMariadb"), <Bool value={db.is_mariadb} key="m" />],
          [t("sourceDetail.datadir"), <span key="d" className="font-mono text-[10px]">{db.datadir}</span>],
          [t("sourceDetail.socket"), <span key="s" className="font-mono text-[10px]">{db.socket}</span>],
          [t("sourceDetail.binlog"), <Bool value={db.binlog_enabled} key="b" />],
          [t("sourceDetail.engines"), db.engines || "?"],
          [t("sourceDetail.nonInnodb"), db.non_innodb || "none"],
          [t("sourceDetail.dataSize"), db.data_size_bytes ? fmtBytes(db.data_size_bytes) : "?"],
        ]} />
      </div>

      <div className="grid-panel" data-testid="preflight-tools">
        <div className="px-5 py-3 border-b border-border"><h3 className="font-display font-bold">{t("sourceDetail.detectedTools")}</h3></div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border">
          {Object.entries(tools).map(([tn, ok]) => (
            <div key={tn} className="bg-card px-4 py-2.5 flex items-center justify-between" title={ver[tn] || ""}>
              <span className="font-mono text-xs">{tn}</span><Bool value={!!ok} />
            </div>
          ))}
        </div>
        {(ver["mariadb-backup"] || ver["mbstream"]) && (
          <div className="px-5 py-3 border-t border-border font-mono text-[11px] text-muted-foreground space-y-0.5">
            {ver["mariadb-backup"] && <p>mariadb-backup: {ver["mariadb-backup"]}</p>}
            {ver["mbstream"] && <p>mbstream: {ver["mbstream"]}</p>}
            {ver["zstd"] && <p>zstd: {ver["zstd"]}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function InfoPanel({ title, rows }) {
  return (
    <div className="grid-panel">
      <div className="px-5 py-3 border-b border-border"><h3 className="font-display font-bold">{title}</h3></div>
      <div className="divide-y divide-border">
        {rows.map(([k, v], i) => (
          <div key={i} className="px-5 py-2.5 flex items-center justify-between gap-3">
            <span className="text-sm text-muted-foreground">{k}</span>
            <span className="text-sm text-right truncate">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ObjGroup({ icon: Icon, title, items }) {
  if (!items?.length) return null;
  return (
    <div>
      <p className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
        <Icon className="h-3 w-3" /> {title} ({items.length})
      </p>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it, i) => (
          <span key={i} className={`font-mono text-xs px-2 py-1 border ${it.warn ? "border-yellow-400 bg-yellow-50 text-yellow-800" : "border-border bg-secondary"}`}
            title={it.meta || ""}>
            {it.name}{it.meta ? ` · ${it.meta}` : ""}
          </span>
        ))}
      </div>
    </div>
  );
}
